import { Controller } from "@hotwired/stimulus"
import { getCsrfToken } from "shared/form_helpers"

/**
 * ProjectSelectionController
 *
 * Handles the project selection page. When a user submits a project card's form,
 * it PUTs to the set_active_project API endpoint via AJAX, consumes the response
 * as JSON, and redirects to the dashboard only once the switch is confirmed.
 *
 * Form submissions are bound DECLARATIVELY via data-action="submit->project-selection#selectProject"
 * in show.html.erb — this controller does not add its own submit listeners (doing so
 * previously double-bound the handler and fired two concurrent PUTs per click).
 *
 * Also provides real-time search filtering across project names and descriptions.
 *
 * Connects to data-controller="project-selection"
 */
export default class extends Controller {
    static targets = ["errorMessage", "searchInput", "projectItem", "projectList", "noResults"]

    // ─── Search / Filter ────────────────────────────────────────────────────

    // Alphabetical order is maintained after filtering because items are rendered
    // in sorted order from the server and show/hide preserves DOM order.
    filterProjects() {
        if (!this.hasSearchInputTarget || !this.hasProjectItemTarget) return

        const query = this.searchInputTarget.value.trim().toLowerCase()
        let visibleCount = 0

        this.projectItemTargets.forEach(form => {
            const name = form.dataset.projectName || ''
            const description = form.dataset.projectDescription || ''
            const matches = !query || name.includes(query) || description.includes(query)

            form.style.display = matches ? '' : 'none'
            if (matches) visibleCount++
        })

        // Show/hide "no results" message
        if (this.hasNoResultsTarget) {
            this.noResultsTarget.classList.toggle('d-none', visibleCount > 0)
        }
    }

    // ─── Project Selection ──────────────────────────────────────────────────

    async selectProject(event) {
        event.preventDefault()

        const form = event.target.closest('form') || event.target
        const projectId = form.querySelector('input[name="project_id"]')?.value

        if (!projectId) {
            this._showError('No project selected.')
            return
        }

        // Disable all buttons while processing
        this._disableButtons()

        try {
            const response = await fetch('/api/projects/set_active_project', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ project_id: projectId }),
                credentials: 'same-origin',

                // User-initiated selection. We should prioritize this action since
                // it directs the user away from this page, after which none of the other actions are necessary
                priority: 'high'
            })

            // Parse the body as JSON defensively — a non-JSON body (e.g. an HTML
            // redirect/error page) must NOT throw an unhandled error here.
            let data = null
            try {
                data = await response.json()
            } catch (_parseError) {
                data = null
            }

            // Only navigate when the server actually confirms the switch: a 2xx response,
            // a success envelope, AND the active project it reports matches what we posted.
            // The envelope shape is { status: 'success', data: { active_project_id } }
            // (see render_success / Api::ProjectsController#select). Trusting response.ok
            // alone let a bounced-back selection look like success.
            const confirmed = response.ok &&
                data &&
                data.status === 'success' &&
                data.data &&
                data.data.active_project_id === projectId

            if (confirmed) {
                // Redirect to dashboard
                window.location.href = '/home/index'
            } else {
                const message = data?.errors?.[0]?.message || data?.message || 'Unable to select project. Please try again.'
                this._showError(message)
                this._enableButtons()
            }
        } catch (error) {
            console.error('Error selecting project:', error)
            this._showError('A network error occurred. Please try again.')
            this._enableButtons()
        }
    }

    // ─── Private Helpers ────────────────────────────────────────────────────

    _disableButtons() {
        this.element.querySelectorAll('button[type="submit"]').forEach(btn => {
            btn.disabled = true
            btn.classList.add('disabled')
        })
    }

    _enableButtons() {
        this.element.querySelectorAll('button[type="submit"]').forEach(btn => {
            btn.disabled = false
            btn.classList.remove('disabled')
        })
    }

    _showError(message) {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.textContent = message
            this.errorMessageTarget.style.display = 'block'
        } else {
            const existingAlert = this.element.querySelector('.project-selection-error')
            if (existingAlert) {
                existingAlert.textContent = message
                existingAlert.style.display = 'block'
            } else {
                const alert = document.createElement('div')
                alert.className = 'alert alert-danger project-selection-error mt-3'
                alert.setAttribute('role', 'alert')
                alert.textContent = message
                this.element.querySelector('.form-body')?.prepend(alert)
            }
        }
    }

}
